// Redacts logger args. Used only by logger.js - call sites never import it.
//
// Backstop only. Never log PHI or credentials on purpose - log ids and counts
// instead. This exists for payloads that can't be vetted field-by-field at the
// call site (pusher events, error payloads). A masked value in dev output means
// the call site should log less, not that redaction made the line fine.
//
// No dependencies on purpose: it ships in the kiosk IIFE bundle via logger.js.

const MASK = '[redacted]'
const MAX_DEPTH = 6
const MAX_STRING_LENGTH = 1000
const SENSITIVE_KEY = /name|mrn|birth|dob|gender|sex|address|phone|email|room|bed|transcript|speech|note|message|text|title|body|token|secret|password|credential|authorization/i

export class Redactor {
  // Returns a copy, so expanded console lines show log-time values.
  sanitize(arg) {
    if (arg?.isAxiosError) {
      return this.summarizeAxiosError(arg)
    }
    return this.redact(arg)
  }

  // An AxiosError carries config.headers, including the Authorization bearer
  // token. Keep only the fields worth reading.
  summarizeAxiosError(error) {
    return {
      status: error.response?.status,
      code: error.code,
      url: error.config?.url,
      message: error.message,
      data: this.redact(error.response?.data)
    }
  }

  redact(value, depth = 0) {
    if (typeof value === 'string') {
      // top-level strings are the message itself - never truncated
      return depth === 0 ? value : this.truncate(value)
    }
    if (value === null || typeof value !== 'object') {
      return value
    }
    if (value instanceof Error) {
      return value
    }
    if (depth >= MAX_DEPTH) {
      return MASK
    }
    if (Array.isArray(value)) {
      return value.map((item) => this.redact(item, depth + 1))
    }
    return this.redactObject(value, depth)
  }

  redactObject(object, depth) {
    const result = {}
    for (const [key, value] of Object.entries(object)) {
      if (SENSITIVE_KEY.test(key) && value != null) {
        result[key] = MASK
      } else {
        result[key] = this.redact(value, depth + 1)
      }
    }
    return result
  }

  truncate(text) {
    if (text.length <= MAX_STRING_LENGTH) {
      return text
    }
    return `${text.slice(0, MAX_STRING_LENGTH)}... [${text.length} chars]`
  }
}
